import { SVGProps } from 'react';
import { createSelector } from 'reselect';

import { ICONS } from '@/components/switchback/Icon/Icon.constants';
import {
  AMENITIES_FOR_CAMPGROUND_ICONS,
  AMENITIES_FOR_CAMPSITES_ICONS,
  AMENITIES_FOR_STATIONARY_CAMPER_CAMPSITES_ICONS,
  AMENITIES_ICONS,
} from '@/constants/amenities';
import { ELocationDetails, LOCATION_DETAILS_ICONS } from '@/constants/locationDetails';
import { TRootState } from '@/redux/rootReducer';
import { TGetRentalItems } from '@/services/types/core/rentalItems';
import { ICampgroundFeatures } from '@/services/types/search/campgrounds/id';
import { TLinensIncluded } from '@/services/types/search/rentals/id';
import { getIntl } from '@/utility/i18n';
import { IAmenityList, mapAmenities } from '@/utility/mapAmenities';

import { getIsCampsite } from './campsite';

export enum ELinenStatus {
  Included = 'included',
  Addon = 'addon',
  NotIncluded = 'not_included',
}

// Mapping the union exhaustively rather than testing one member: the compiler fails this Record the
// next time the backend adds a linen state, instead of that state silently resolving to a status.
const LINEN_STATUS_BY_FEATURE: Record<TLinensIncluded, ELinenStatus> = {
  included: ELinenStatus.Included,
  addon: ELinenStatus.Addon,
  not_provided: ELinenStatus.NotIncluded,
};

export const getLinenStatus = createSelector<
  TRootState,
  TRootState['listing']['data'],
  TGetRentalItems,
  ELinenStatus | null
>(
  state => state.listing.data,
  state => state.rentalItems.data,
  (listing, rentalItems): ELinenStatus | null => {
    // `== null` rather than `=== undefined`: a listing predating the setting may arrive with the
    // field either omitted or explicitly null, and both mean "the host never answered".
    const linensIncluded = listing?.features?.linens_included;

    if (linensIncluded == null) {
      return null;
    }

    // Annotated as optional because the API response is not validated at runtime — a state added
    // backend-side reaches this lookup before the union above knows about it.
    const status: ELinenStatus | undefined = LINEN_STATUS_BY_FEATURE[linensIncluded];

    // Say nothing about an unrecognized state rather than assert a negative: NotIncluded is not
    // silence, it renders "Linens are not included" in the sleeps tooltip (see rentalDetails.ts).
    if (status === undefined) {
      return null;
    }

    // A linens rental item is bookable regardless of the setting, so it upgrades the only state the
    // host has left open. Their explicit included/addon answer wins over it.
    if (
      status === ELinenStatus.NotIncluded &&
      rentalItems?.some(item => item.category === 'linens')
    ) {
      return ELinenStatus.Addon;
    }

    return status;
  },
);

type TListingData = TRootState['listing']['data'];

export interface ICategory {
  id: string;
  name: string;
}

export interface IAmenity {
  available: boolean;
  category: string;
  id: string;
  name: string;
  icon?: ICONS;
  BonfireIcon?: (props: SVGProps<SVGSVGElement>) => JSX.Element;
  unavailableIcon?: ICONS;
}

export const getAmenities = (state: TRootState): IAmenityList => {
  return mapAmenities(
    state.listing.data?.catalog?.processed_amenities,
    state.listing.data?.catalog?.amenities_categories,
    AMENITIES_ICONS,
  );
};

export const getAmenitiesForListing = createSelector<
  TRootState,
  boolean,
  TRootState['listing']['data'],
  IAmenityList
>(
  getIsCampsite,
  state => state.listing.data,
  (isCampsite, listing) => {
    if (isCampsite) {
      return mapAmenities(
        listing?.campsite_category_catalog?.processed_amenities,
        listing?.campsite_category_catalog?.amenities_categories,
        listing?.campsite_category?.category_type === 'stationary_camper_site'
          ? AMENITIES_FOR_STATIONARY_CAMPER_CAMPSITES_ICONS
          : AMENITIES_FOR_CAMPSITES_ICONS,
      );
    }

    const mappedAmenities = mapAmenities(
      listing?.catalog?.processed_amenities,
      listing?.catalog?.amenities_categories,
      AMENITIES_ICONS,
    );

    const hookupElectric = mappedAmenities.list.find(amenity => amenity.id === 'hookup_electric');

    if (hookupElectric && hookupElectric.available && listing?.vehicle_amps) {
      const intl = getIntl();

      hookupElectric.name += ` (${listing.vehicle_amps} ${intl.formatMessage({
        defaultMessage: 'Amps',
        id: 'tXLV+z',
      })})`;
    }

    return mappedAmenities;
  },
);

export const getCampsiteCampgroundAmenities = (state: TRootState): IAmenityList => {
  return mapAmenities(
    state.listing.data?.campground_catalog?.processed_amenities,
    state.listing.data?.campground_catalog?.amenities_categories,
    AMENITIES_FOR_CAMPGROUND_ICONS,
  );
};

export const getCampgroundAmenities = (state: TRootState): IAmenityList => {
  return mapAmenities(
    state.campgroundListing.data?.campground_catalog?.processed_amenities,
    state.campgroundListing.data?.campground_catalog?.amenities_categories,
    AMENITIES_FOR_CAMPGROUND_ICONS,
  );
};

export const getCampgroundLocationDetailsAmenityList = createSelector<
  TRootState,
  TListingData,
  IAmenityList
>(
  state => state.listing.data,
  data => {
    const intl = getIntl();

    if (!data) {
      return {
        categories: [],
        list: [],
      };
    }

    const categories = [
      {
        id: 'scenery',
        name: intl.formatMessage({ defaultMessage: 'Scenery', id: 'ky9r22' }),
      },
      {
        id: 'activities',
        name: intl.formatMessage({ defaultMessage: 'Activities', id: 'UmEsZF' }),
      },
    ];

    const activitesList = (data.campground_catalog?.activity_features || []).map(activity => ({
      category: 'activities',
      available: Boolean(
        data.campground?.features?.[activity.feature as keyof ICampgroundFeatures],
      ),
      id: activity.feature,
      name: activity.name,
      BonfireIcon: LOCATION_DETAILS_ICONS[activity.feature as ELocationDetails],
    }));

    const sceneryList = (data.campground_catalog?.scenery_features || []).map(scenery => ({
      category: 'scenery',
      available: Boolean(data.campground?.features?.[scenery.feature as keyof ICampgroundFeatures]),
      id: scenery.feature,
      name: scenery.name,
      BonfireIcon: LOCATION_DETAILS_ICONS[scenery.feature as ELocationDetails],
    }));

    return {
      categories,
      list: [...sceneryList, ...activitesList],
    };
  },
);
